import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Drawer, InlineError, Input, Skeleton } from '../../components/ui';
import { useLinkedInDays, useSaveLinkedInDay } from './hooks';
import { todayIso } from './trackerFormat';
import { linkedInStats, salesErrorMessage, splitNoteTags } from './types';

function NoteTags({ notes }: { notes: string }) {
  const tags = splitNoteTags(notes);
  if (tags.length === 0) {
    return null;
  }
  return (
    <span className="note-tag-row">
      {tags.map((tag) => (
        <span key={tag} className="note-tag">
          {tag}
        </span>
      ))}
    </span>
  );
}

/**
 * The LinkedIn daily counter: connections, DMs, replies per day.
 *
 * Since the Prospecting tab (design note drop 2) it is the line at the top
 * of that tab, and `children` — the prospect list — renders between the log
 * button and the day-by-day history.
 */
export function LinkedInView({ children }: { children?: ReactNode }) {
  const daysQuery = useLinkedInDays();
  const save = useSaveLinkedInDay();
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [logOpen, setLogOpen] = useState(false);
  const [entryDate, setEntryDate] = useState(todayIso());
  const [form, setForm] = useState({
    connections_sent: '0',
    cs_notes: '',
    new_connections: '0',
    nc_notes: '',
    dms_sent: '0',
    dm_notes: '',
    messages_received: '0',
    mr_notes: '',
  });

  if (daysQuery.isPending) return <Skeleton lines={4} label="Loading LinkedIn activity" />;

  const days = daysQuery.data ?? [];
  const stats = linkedInStats(days);

  function loadDay(iso: string) {
    setLogOpen(true);
    setEntryDate(iso);
    const existing = days.find((day) => day.entry_date === iso);
    setForm({
      connections_sent: String(existing?.connections_sent ?? 0),
      cs_notes: existing?.cs_notes ?? '',
      new_connections: String(existing?.new_connections ?? 0),
      nc_notes: existing?.nc_notes ?? '',
      dms_sent: String(existing?.dms_sent ?? 0),
      dm_notes: existing?.dm_notes ?? '',
      messages_received: String(existing?.messages_received ?? 0),
      mr_notes: existing?.mr_notes ?? '',
    });
    setMessage('');
  }

  function saveDay() {
    const numbers = {
      connections_sent: Number(form.connections_sent),
      new_connections: Number(form.new_connections),
      dms_sent: Number(form.dms_sent),
      messages_received: Number(form.messages_received),
    };
    if (Object.values(numbers).some((value) => Number.isNaN(value) || value < 0)) {
      setError('Counts must be zero or more.');
      return;
    }
    setError('');
    save.mutate(
      {
        entry_date: entryDate,
        ...numbers,
        cs_notes: form.cs_notes,
        nc_notes: form.nc_notes,
        dm_notes: form.dm_notes,
        mr_notes: form.mr_notes,
      },
      {
        onSuccess: () => setMessage(`Saved ${entryDate}.`),
        onError: (cause) => setError(salesErrorMessage(cause.message)),
      },
    );
  }

  const numberField = (
    label: string,
    key: 'connections_sent' | 'new_connections' | 'dms_sent' | 'messages_received',
    notesKey: 'cs_notes' | 'nc_notes' | 'dm_notes' | 'mr_notes',
    notesLabel: string,
  ) => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        gap: 'var(--space-4)',
        alignItems: 'start',
      }}
    >
      <Input
        type="number"
        label={label}
        value={form[key]}
        onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
      />
      <Input
        label={notesLabel}
        value={form[notesKey]}
        onChange={(e) => setForm((prev) => ({ ...prev, [notesKey]: e.target.value }))}
      />
    </div>
  );

  return (
    <div>
      {/* KPI strip */}
      <div className="tracker-dashboard-card" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="tracker-inbound-strip">
          <div className="tracker-inbound-stat">
            <p className="tracker-inbound-value">{stats.connectionsSent}</p>
            <p className="tracker-inbound-label">Connections Sent</p>
          </div>
          <div className="tracker-inbound-stat">
            <p className="tracker-inbound-value">{stats.newConnections}</p>
            <p className="tracker-inbound-label">New Connections</p>
          </div>
          <div className="tracker-inbound-stat">
            <p className="tracker-inbound-value">{stats.acceptanceRate}%</p>
            <p className="tracker-inbound-label">Acceptance Rate</p>
          </div>
          <div className="tracker-inbound-stat">
            <p className="tracker-inbound-value">{stats.dmsSent}</p>
            <p className="tracker-inbound-label">First-Touch DMs</p>
          </div>
          <div className="tracker-inbound-stat">
            <p className="tracker-inbound-value">{stats.messagesReceived}</p>
            <p className="tracker-inbound-label">Messages Received</p>
          </div>
          <div className="tracker-inbound-stat">
            <p className="tracker-inbound-value">{stats.avgConnectionsPerDay}</p>
            <p className="tracker-inbound-label">Avg Connections / Day</p>
          </div>
        </div>
      </div>

      {/* Log a day: header action + drawer (redesign spec) */}
      <div className="pipeline-actions">
        <Button variant="secondary" size="sm" onClick={() => loadDay(todayIso())}>
          Log activity
        </Button>
      </div>
      <Drawer open={logOpen} title="Log LinkedIn activity" onClose={() => setLogOpen(false)}>
        <p
          style={{
            margin: '0 0 var(--space-4)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
          }}
        >
          One entry per day — picking an existing date edits that day. Separate notes with commas to
          log them as tags.
        </p>
        {error !== '' && <InlineError>{error}</InlineError>}
        <Input
          type="date"
          label="Day"
          value={entryDate}
          onChange={(e) => loadDay(e.target.value)}
        />
        {numberField(
          'Connections sent',
          'connections_sent',
          'cs_notes',
          'Notes (targets, companies)',
        )}
        {numberField('New connections (accepted)', 'new_connections', 'nc_notes', 'Notes')}
        {numberField('First-touch DMs sent', 'dms_sent', 'dm_notes', 'Notes')}
        {numberField(
          'Messages received',
          'messages_received',
          'mr_notes',
          'Notes (who, signal quality)',
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <Button onClick={saveDay} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save day'}
          </Button>
          {message !== '' && (
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
              {message}
            </span>
          )}
        </div>
      </Drawer>

      {children}

      {/* History */}
      <div className="tracker-dashboard-card">
        <p className="tracker-dashboard-card-title">History — click a row to edit</p>
        {days.length === 0 ? (
          <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            No activity logged yet.
          </p>
        ) : (
          <table className="tracker-perf-table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Connections Sent</th>
                <th>New Connections</th>
                <th>DMs Sent</th>
                <th>Messages Received</th>
              </tr>
            </thead>
            <tbody>
              {days.map((day) => (
                <tr
                  key={day.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => loadDay(day.entry_date)}
                >
                  <td style={{ whiteSpace: 'nowrap' }}>{day.entry_date}</td>
                  <td>
                    {day.connections_sent}
                    <NoteTags notes={day.cs_notes} />
                  </td>
                  <td>
                    {day.new_connections}
                    <NoteTags notes={day.nc_notes} />
                  </td>
                  <td>
                    {day.dms_sent}
                    <NoteTags notes={day.dm_notes} />
                  </td>
                  <td>
                    {day.messages_received}
                    <NoteTags notes={day.mr_notes} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Reports ───────────────────────────────────────────────────────────────────

/** Hands a CSV to the browser as a download. */
